import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  FileText,
  Plus,
  Trash2,
  Wallet,
  Clock,
  Eye,
  Download,
  Copy,
  ExternalLink,
  Search,
  X,
  Building2,
  Phone,
  ChevronLeft,
  ChevronRight,
  CalendarRange,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR, formatDocTotal, docInrEquivalent } from '@/shared/utils/currency';
import { ratePerBaseUnit } from '@/shared/utils/ratePerBaseUnit';
import { ROUTES } from '@/routes/paths';
import { RecordAdvanceModal } from '@/features/sales/components/RecordAdvanceModal';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import {
  useQuotationsPaged,
  useQuotationMutations,
  type Quotation,
  type QuotationLine,
  type QuotationSort,
} from '../hooks/useQuotations';

/** Columns the backend allowlists for sorting (ORDER_SORTS, src/api-sales/orders.js). */
type SortColumn = 'customer' | 'amount' | 'date';
const SORT_ASC: Record<SortColumn, QuotationSort> = {
  customer: 'customer_asc',
  amount: 'amount_asc',
  date: 'date_asc',
};
const SORT_DESC: Record<SortColumn, QuotationSort> = {
  customer: 'customer_desc',
  amount: 'amount_desc',
  date: 'date_desc',
};

interface DraftLine extends QuotationLine {
  key: string;
  name: string;
  /**
   * Checklist item 44 — a line added in this form must be a real catalogue
   * product, not free text, so a quick quotation carries the same HSN, unit and
   * pack detail as one built in the full editor. Lines loaded from an existing
   * quotation are not marked: refusing to save one the user never touched would
   * lock them out of editing their own document.
   */
  needsProduct?: boolean;
}

const emptyLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  productId: '',
  needsProduct: true,
  name: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  discountType: 'percent',
  cgstRate: 0,
  sgstRate: 0,
  igstRate: 0,
});

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function QuotationsPage() {
  const navigate = useNavigate();
  // Read-once snapshot of the URL this page was opened with — every filter
  // below seeds its initial state from it, so a shared link, a refresh, or
  // the browser Back button reopens the same view instead of resetting to
  // defaults. `setSearchParams` (the write side) is used only via the effect
  // below, never to re-read — reading `searchParams` reactively here would
  // fight that same effect on every render.
  const initialParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isFormOpen = creating || editingId !== null;
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);

  // Filtering, Search & Modal State
  const [searchInput, setSearchInput] = useState(() => initialParams.get('q') || '');
  const [searchQuery, setSearchQuery] = useState(() => initialParams.get('q') || '');
  // A comma-separated status list arrives from the dashboard's Open
  // Quotations tile (e.g. ?status=draft,quotation) so the tile's count and
  // this list's rows can never disagree — see src/shared/dashboard/kpi.js.
  const [statusFilter, setStatusFilter] = useState<string>(
    () => initialParams.get('status') || 'all',
  );
  const [fromDate, setFromDate] = useState(() => initialParams.get('from') || '');
  const [toDate, setToDate] = useState(() => initialParams.get('to') || '');
  const [sort, setSort] = useState<QuotationSort>(
    () => (initialParams.get('sort') as QuotationSort) || 'date_desc',
  );
  const [page, setPage] = useState(() => Number(initialParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(50);
  const [previewQuotation, setPreviewQuotation] = useState<Quotation | null>(null);
  const [advanceQuotation, setAdvanceQuotation] = useState<Quotation | null>(null);

  const currentUser = useUser();
  const isCoordinator = currentUser?.role === 'sales_coordinator';
  const canSeeWholeDirectory = [
    'superadmin',
    'admin',
    'department_admin',
    'sales_coordinator',
  ].includes(currentUser?.role || '');
  const { data: userOptions = [] } = useUserOptions(canSeeWholeDirectory);
  const [createdBy, setCreatedBy] = useState(
    () => initialParams.get('createdBy') || (isCoordinator ? currentUser?.id || '' : ''),
  );
  const [userSelectedFilter, setUserSelectedFilter] = useState(false);

  useEffect(() => {
    if (
      isCoordinator &&
      !initialParams.get('createdBy') &&
      !userSelectedFilter &&
      !createdBy &&
      currentUser?.id
    ) {
      setCreatedBy(currentUser.id);
    }
  }, [isCoordinator, currentUser?.id, userSelectedFilter, createdBy]);

  // Mirror the live filters back into the URL (replacing, not pushing, so
  // Back doesn't step through every keystroke) — the write half of the
  // restore-from-URL behaviour above.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (sort !== 'date_desc') params.set('sort', sort);
    if (createdBy) params.set('createdBy', createdBy);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [searchQuery, statusFilter, fromDate, toDate, sort, createdBy, page, setSearchParams]);

  const toggleSort = (column: SortColumn) => {
    setSort((cur) => (cur === SORT_DESC[column] ? SORT_ASC[column] : SORT_DESC[column]));
  };
  const sortIcon = (column: SortColumn) => {
    if (sort === SORT_ASC[column]) return <ArrowUp className="h-3 w-3" />;
    if (sort === SORT_DESC[column]) return <ArrowDown className="h-3 w-3" />;
    return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  };

  // Debounce the search box so the ~4600-quotation history isn't re-queried
  // on every keystroke — only once typing pauses for 350ms.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any filter change starts back at page 1 — staying on page 6 of a filter
  // that now only has 2 pages would just show an empty table.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, fromDate, toDate, pageSize, sort, createdBy]);

  // Chromium's Print/Save-as-PDF dialog names the file after the TOP window's
  // title, not the iframe's — so the preview's own document title (the real
  // quotation number) is ignored and "RBM Oil — by Zynx" (index.html)
  // shows up instead. Overriding the tab title while the preview is open
  // fixes the filename without touching how printing itself works.
  useEffect(() => {
    if (!previewQuotation) return;
    const original = document.title;
    document.title = previewQuotation.orderNumber || previewQuotation.documentNumber || 'Quotation';
    return () => {
      document.title = original;
    };
  }, [previewQuotation]);

  const {
    data: pagedResult,
    isLoading,
    isFetching,
  } = useQuotationsPaged({
    status: statusFilter,
    search: searchQuery,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    sort,
    createdBy: canSeeWholeDirectory ? createdBy || undefined : undefined,
    page,
    limit: pageSize,
  });
  const quotations = pagedResult?.data ?? [];
  const pagination = pagedResult?.pagination;
  const totalValue = pagedResult?.stats?.totalValue ?? 0;
  const openCount = pagedResult?.stats?.openCount ?? 0;
  const { data: productData } = useProducts();
  const { data: dealsData } = useDeals();
  const { create, update, markLost } = useQuotationMutations();
  const [selectedForLostModal, setSelectedForLostModal] = useState<Quotation | null>(null);

  const renderStatusBadge = (q: Quotation) => {
    const isConfirmed = q.status === 'confirmed' || q.status === 'accepted';
    const isLost = q.status === 'cancelled' || !!q.lostReasonName;

    if (isConfirmed) {
      return (
        <Badge tone="success" className="inline-flex w-fit items-center gap-1">
          <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" />
          Confirmed
        </Badge>
      );
    }

    if (isLost) {
      const reasonLower = (q.lostReasonName || q.lostReasonNote || '').toLowerCase();
      let label: string;
      if (reasonLower.includes('price')) label = 'Closed Lost: Price Issue';
      else if (reasonLower.includes('quality')) label = 'Closed Lost: Poor Quality';
      else if (q.lostReasonName) label = `Closed Lost: ${q.lostReasonName}`;
      else if (q.lostReasonNote) label = `Closed Lost: ${q.lostReasonNote}`;
      else label = 'Closed Lost: Other Issues';

      return (
        <Badge tone="danger" className="inline-flex w-fit items-center gap-1">
          <XCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
          {label}
        </Badge>
      );
    }

    if (q.status === 'draft') {
      return (
        <Badge tone="neutral" className="inline-flex w-fit items-center gap-1">
          Draft
        </Badge>
      );
    }

    // Converted into a Tax Invoice — the most important state a quote can
    // reach, and it used to fall through to the generic "Quotation" badge.
    if (q.status === 'invoiced' || q.status === 'fulfilled') {
      return (
        <Badge tone="primary" className="inline-flex w-fit items-center gap-1">
          <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" />
          Invoiced
        </Badge>
      );
    }

    return (
      <Badge tone="info" className="inline-flex w-fit items-center gap-1">
        Quotation
      </Badge>
    );
  };

  const sourceDealId = new URLSearchParams(window.location.search).get('dealId');
  const sourceDeal = useMemo(
    () => dealsData?.deals?.find((deal) => deal.id === sourceDealId),
    [dealsData?.deals, sourceDealId],
  );

  useEffect(() => {
    if (!sourceDeal || isFormOpen) return;
    setCustomerName(sourceDeal.contact_name || sourceDeal.business_name || sourceDeal.title);
    setCustomerEmail(sourceDeal.email || '');
    setNotes(`Created from deal: ${sourceDeal.title} (${sourceDeal.id})`);
    setCreating(true);
  }, [sourceDeal, isFormOpen]);

  // A quotation is edited in the full form now, not in this page's small inline
  // panel — the panel could not carry the client account, the bill-to snapshot
  // or the per-line GST the document actually needs. Any surviving ?edit=<id>
  // link (bookmarks, the older profile-page button) is forwarded there.
  const editParamId = new URLSearchParams(window.location.search).get('edit');
  useEffect(() => {
    if (editParamId) navigate(`/quotations/${editParamId}/edit`, { replace: true });
  }, [editParamId, navigate]);

  const products = useMemo(
    () =>
      (productData?.data ?? []) as unknown as Array<{
        _id: string;
        name: string;
        sellingPrice?: number;
        hsnSac?: string;
        cgstRate?: number;
        sgstRate?: number;
        igstRate?: number;
        unit?: string;
        packSize?: string;
        qtyPerPack?: number;
        totalPackSize?: string;
        volumeLiters?: number;
      }>,
    [productData],
  );

  const productOptions = useMemo(
    () =>
      products
        .map((p) => ({ label: p.name, value: p._id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [products],
  );

  const setLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const pickProduct = (key: string, productId: string) => {
    const p = products.find((x) => x._id === productId);
    if (!p) return;
    setLine(key, {
      productId: p._id,
      name: p.name,
      description: p.name,
      unitPrice: p.sellingPrice ?? 0,
      cgstRate: p.cgstRate ?? 0,
      sgstRate: p.sgstRate ?? 0,
      igstRate: p.igstRate ?? 0,
      // Item 44: these were dropped, which is why a quick quotation printed with
      // no HSN and no pack detail while the same line built in the full editor
      // carried both.
      hsnSac: p.hsnSac ?? '',
      unit: p.unit ?? '',
      packSize: p.packSize ?? '',
      qtyPerPack: p.qtyPerPack ?? 0,
      totalPackSize: p.totalPackSize ?? '',
      volumeLiters: p.volumeLiters ?? 0,
      needsProduct: false,
    });
  };

  const lineNet = (l: DraftLine) => {
    const gross = (l.quantity || 0) * (l.unitPrice || 0);
    const disc = l.discountType === 'percent' ? gross * ((l.discount || 0) / 100) : l.discount || 0;
    return Math.max(0, gross - disc);
  };

  const lineTax = (l: DraftLine) => {
    const net = lineNet(l);
    const rate = (l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0);
    return net * (rate / 100);
  };

  const draftSubtotal = useMemo(() => lines.reduce((s, l) => s + lineNet(l), 0), [lines]);
  const draftTax = useMemo(() => lines.reduce((s, l) => s + lineTax(l), 0), [lines]);

  const canSubmit =
    customerName.trim().length > 0 &&
    lines.some((l) => (l.description || l.name) && l.unitPrice > 0) &&
    // Item 44 — every line added here must be chosen from the catalogue, so the
    // quick quotation and the full editor produce the same document.
    lines.every((l) => !l.needsProduct || Boolean(l.productId));

  const reset = () => {
    setCreating(false);
    setEditingId(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setNotes('');
    setLines([emptyLine()]);
  };

  const submit = async () => {
    const payload = {
      customerName,
      customerEmail: customerEmail || undefined,
      customerPhone: customerPhone || undefined,
      notes: notes || undefined,
      // Only stamped on create — editingId means this quotation already
      // exists and its crmDealId (if any) shouldn't be overwritten by
      // whatever ?dealId= happens to be in the URL during an edit.
      crmDealId: !editingId && sourceDealId ? sourceDealId : undefined,
      lines: lines.map((l) => ({
        productId: l.productId || undefined,
        description: l.description || l.name,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        discount: l.discount,
        discountType: l.discountType,
        cgstRate: l.cgstRate,
        sgstRate: l.sgstRate,
        igstRate: l.igstRate,
        hsnSac: l.hsnSac,
        unit: l.unit,
        packSize: l.packSize,
        qtyPerPack: l.qtyPerPack,
        totalPackSize: l.totalPackSize,
        volumeLiters: l.volumeLiters,
        lineTotal: lineNet(l) + lineTax(l),
      })),
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, payload });
        toast.success('Quotation updated successfully');
      } else {
        await create.mutateAsync(payload);
        toast.success('Quotation created successfully');
      }
      reset();
    } catch (err) {
      toast.error(errorText(err, 'Failed to save quotation'));
    }
  };

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16 font-sans text-on-surface">
      <PageHeader
        icon={FileText}
        eyebrow="Sales"
        title="Quotations"
        description="Every quotation sent to a client — search, download, clone or convert it to a proforma or invoice."
        actions={
          <Button
            // The field-based form (ROUTES.quotationNew), not the Studio
            // sheet — see QuotationFormPage's header for why. The Studio is
            // still one click away from inside the form.
            onClick={() => {
              const url = sourceDealId
                ? `${ROUTES.quotationNew}?dealId=${encodeURIComponent(sourceDealId)}`
                : ROUTES.quotationNew;
              navigate(url);
            }}
          >
            <Plus className="h-4 w-4" />
            New Quotation
          </Button>
        }
      />

      <StatGrid cols={3}>
        <StatCard
          label="Total Quotations"
          value={pagination?.total ?? quotations.length}
          hint="All quotations on record"
          icon={FileText}
          tone="primary"
        />
        <StatCard
          label="Total Quoted Value"
          value={formatINR(totalValue)}
          hint="Across all clients · USD/EUR counted in ₹"
          icon={Wallet}
          tone="info"
        />
        <StatCard
          label="Open / Pending"
          value={openCount}
          hint="Awaiting client response"
          icon={Clock}
          tone="warning"
        />
      </StatGrid>

      {/* Quotation Create / Edit Drawer */}
      {isFormOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-md"
        >
          <div className="flex items-center justify-between border-b border-border pb-3">
            <h2 className="text-sm font-extrabold uppercase text-on-surface">
              {editingId ? 'Edit Quotation Details' : 'Create New Quotation'}
            </h2>
            <button onClick={reset} className="text-on-surface-subtle hover:text-on-surface-variant">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-on-surface-variant">
                Customer / Company *
              </label>
              <Input
                placeholder="e.g. Apex Industrial Lubricants Ltd"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-on-surface-variant">Client Email</label>
              <Input
                placeholder="client@company.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-on-surface-variant">Client Phone</label>
              <Input
                placeholder="+91 98765 43210"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <div className="text-xs font-bold uppercase text-on-surface-variant">Line Items</div>
            {lines.map((l) => (
              <div
                key={l.key}
                className="grid items-center gap-2 rounded-xl border border-border bg-muted p-2 sm:grid-cols-12"
              >
                <div className="space-y-1 sm:col-span-5">
                  <Input
                    placeholder="Product / Oil Grade / Description *"
                    value={l.description || ''}
                    onChange={(e) => setLine(l.key, { description: e.target.value })}
                  />
                  {productOptions.length > 0 && (
                    <Select
                      value={l.productId ?? ''}
                      placeholder="…or pick catalog item"
                      options={productOptions}
                      onChange={(e) => pickProduct(l.key, e.target.value)}
                    />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Input
                    type="number"
                    min={1}
                    placeholder="Qty"
                    value={l.quantity}
                    onChange={(e) => setLine(l.key, { quantity: Number(e.target.value) })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    type="number"
                    min={0}
                    placeholder="Rate (₹)"
                    value={l.unitPrice}
                    onChange={(e) => setLine(l.key, { unitPrice: Number(e.target.value) })}
                  />
                  {ratePerBaseUnit(
                    l.unitPrice,
                    products.find((p) => p._id === l.productId),
                  ) && (
                    <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                      {ratePerBaseUnit(
                        l.unitPrice,
                        products.find((p) => p._id === l.productId),
                      )}
                    </p>
                  )}
                </div>
                <div className="text-right text-xs font-bold text-on-surface sm:col-span-2">
                  {formatINR(lineNet(l))}
                </div>
                <div className="flex justify-end sm:col-span-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="secondary"
              onClick={() => setLines((ls) => [...ls, emptyLine()])}
              className="text-xs font-bold"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Product Line
            </Button>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold text-on-surface-variant">Notes / Terms</label>
            <Input
              placeholder="Payment terms, delivery timeline, validity..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div className="text-xs">
              <span className="text-on-surface-variant">Subtotal: </span>
              <strong>{formatINR(draftSubtotal)}</strong>
              <span className="text-on-surface-variant"> + Taxes: </span>
              <strong>{formatINR(draftTax)}</strong>
              <span className="text-on-surface-variant"> = Grand Total: </span>
              <strong className="text-base font-black text-primary">
                {formatINR(draftSubtotal + draftTax)}
              </strong>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={reset} className="text-xs">
                Cancel
              </Button>
              <Button
                onClick={submit}
                loading={create.isPending || update.isPending}
                disabled={!canSubmit}
                className="bg-primary text-xs font-bold text-white shadow-sm hover:bg-primary-dim"
              >
                {editingId ? 'Update Quotation' : 'Save & Issue Quotation'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Search & Filter Bar */}
      <div className="space-y-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative min-w-[240px] max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by Quote No (e.g. 4264), Client Name, Phone..."
              className="w-full rounded-lg border border-input bg-muted py-2 pl-9 pr-3 text-body-sm text-on-surface placeholder:text-on-surface-subtle transition focus:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs">
              <CalendarRange className="h-3.5 w-3.5 text-on-surface-subtle" />
              <input
                type="date"
                lang="en-IN"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs text-on-surface-variant transition focus:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <span className="text-on-surface-subtle">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs text-on-surface-variant transition focus:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {(fromDate || toDate) && (
                <button
                  onClick={() => {
                    setFromDate('');
                    setToDate('');
                  }}
                  className="text-on-surface-subtle hover:text-on-surface-variant"
                  aria-label="Clear date range" title="Clear date range"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Status Quick Filter Pills + Dropdown Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-xl border border-border bg-muted p-1 text-xs font-bold">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={cn(
                    'rounded-lg px-2.5 py-1 transition',
                    statusFilter === 'all'
                      ? 'shadow-xs bg-card text-primary'
                      : 'text-on-surface-variant hover:text-on-surface',
                  )}
                >
                  {`All (${(statusFilter === 'all' && pagination ? pagination.total : quotations.length).toLocaleString('en-IN')})`}
                </button>
                <button
                  onClick={() => setStatusFilter('quotation')}
                  className={cn(
                    'rounded-lg px-2.5 py-1 transition',
                    statusFilter === 'quotation'
                      ? 'shadow-xs bg-card text-primary'
                      : 'text-on-surface-variant hover:text-on-surface',
                  )}
                >
                  {`Quotations (${openCount.toLocaleString('en-IN')})`}
                </button>
                <button
                  onClick={() => setStatusFilter('confirmed')}
                  className={cn(
                    'rounded-lg px-2.5 py-1 transition',
                    statusFilter === 'confirmed'
                      ? 'shadow-xs bg-card text-success-text'
                      : 'text-on-surface-variant hover:text-on-surface',
                  )}
                >
                  Confirmed
                </button>
                <button
                  onClick={() => setStatusFilter('lost')}
                  className={cn(
                    'rounded-lg px-2.5 py-1 transition',
                    statusFilter.startsWith('lost')
                      ? 'shadow-xs bg-card text-danger-text'
                      : 'text-on-surface-variant hover:text-on-surface',
                  )}
                >
                  Lost
                </button>
              </div>

              {/* Only display the filter dropdown when "Lost" is selected, and only show closed lost options */}
              {statusFilter.startsWith('lost') && (
                <div className="w-56">
                  <Select
                    name="quotations-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                      { value: 'lost', label: 'Closed Lost (All)' },
                      { value: 'lost:price_issue', label: 'Closed Lost: Price Issue' },
                      { value: 'lost:poor_quality', label: 'Closed Lost: Poor Quality' },
                      { value: 'lost:other', label: 'Closed Lost: Other Issues' },
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Whole-directory roles only — a scoped rep's own list is
                already restricted to themself, so this would be a dropdown
                of one. */}
            {canSeeWholeDirectory && (
              <div className="w-56">
                <Select
                  name="quotations-created-by-filter"
                  value={createdBy}
                  onChange={(e) => {
                    setUserSelectedFilter(true);
                    setCreatedBy(e.target.value);
                  }}
                  options={[
                    { value: '', label: 'All employees' },
                    ...(currentUser?.id
                      ? [
                          {
                            value: currentUser.id,
                            label: `My Quotations (${currentUser.displayName || currentUser.username})`,
                          },
                        ]
                      : []),
                    ...userOptions
                      .filter((u) => u.id !== currentUser?.id)
                      .map((u) => ({ value: u.id, label: u.display_name })),
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quotations List — a compact table. All conversion/edit actions live
          on the details page now; the row only offers View and Download. */}
      {isLoading ? (
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs" aria-busy="true" aria-live="polite">
          <div className="border-b border-border bg-muted px-4 py-3">
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="divide-y divide-border">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-4 w-20 rounded-full" />
                <Skeleton className="h-3.5 w-48" />
                <Skeleton className="ml-auto h-3.5 w-28" />
              </div>
            ))}
          </div>
          <span className="sr-only">Loading quotations…</span>
        </div>
      ) : quotations.length === 0 ? (
        (searchQuery || fromDate || toDate || statusFilter !== 'all') ? (
          <EmptyState
            title="No quotations match these filters"
            description="Nothing was found for the current search and filters. Clear them to see the full list."
            action={
              <Button variant="outline" onClick={() => { setSearchInput(''); setStatusFilter('all'); setFromDate(''); setToDate(''); }}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No quotations yet"
            description="Every tax invoice starts here. Save a draft now, then issue it to the client."
            action={<Button onClick={() => setCreating(true)}>New Quotation</Button>}
          />
        )
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
          <div className={cn('overflow-x-auto transition-opacity', isFetching && 'opacity-60')}>
            <table className="w-full min-w-[860px] text-left text-body-sm">
              <thead>
                <tr className="border-b border-border text-label-sm uppercase tracking-wider text-on-surface-variant">
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pl-4 pr-3">QT Number</th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Status</th>
                  <th
                    scope="col"

                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 hover:text-on-surface"
                    onClick={() => toggleSort('customer')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Client Name {sortIcon('customer')}
                    </span>
                  </th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Phone</th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Email</th>
                  <th
                    scope="col"

                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 text-right hover:text-on-surface"
                    onClick={() => toggleSort('amount')}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Amount {sortIcon('amount')}
                    </span>
                  </th>
                  <th
                    scope="col"

                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 hover:text-on-surface"
                    onClick={() => toggleSort('date')}
                  >
                    <span className="inline-flex items-center gap-1">Date {sortIcon('date')}</span>
                  </th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quotations.map((q) => {
                  const quoteNumber = q.orderNumber || q.documentNumber || q._id.slice(-6);
                  return (
                    <tr
                      key={q._id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/quotations/${q._id}`)}
                      onKeyDown={(e) =>
                        (e.key === 'Enter' || e.key === ' ') && navigate(`/quotations/${q._id}`)
                      }
                      className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/50"
                    >
                      <td className="whitespace-nowrap py-3 pl-4 pr-3">
                        <span className="font-extrabold text-on-surface">{quoteNumber}</span>
                        {q.createdByName && (
                          <span className="block text-data-xs font-normal text-on-surface-subtle">
                            By: {q.createdByName}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-3">{renderStatusBadge(q)}</td>
                      <td className="py-3 pr-3">
                        <span className="flex items-center gap-1 font-bold text-on-surface">
                          <Building2 className="h-3.5 w-3.5 shrink-0 text-on-surface-subtle" />
                          {q.customerName || 'No Client Name'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-3 text-on-surface-variant">
                        {q.customerPhone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0 text-on-surface-subtle" />
                            {q.customerPhone}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 pr-3 text-on-surface-variant">{q.customerEmail || '—'}</td>
                      <td className="whitespace-nowrap py-3 pr-3 text-right font-mono tabular-nums text-data-md font-semibold text-on-surface">
                        {formatDocTotal(q as never)}
                        {docInrEquivalent(q as never) && (
                          <div className="text-data-xs font-medium text-on-surface-subtle">
                            ≈ {docInrEquivalent(q as never)}
                          </div>
                        )}
                        {Number((q as any).amountPaid || 0) > 0 && (
                          <div className="text-[10px] font-bold text-success-text">
                            Adv: {formatINR(Number((q as any).amountPaid))}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-3 text-on-surface-variant">
                        {new Date(q.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setPreviewQuotation(q)}
                            aria-label="View PDF" title="View PDF"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-primary transition hover:bg-primary-container"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          <a
                            href={`/api/documents/quotation/${q._id}?format=pdf&download=1`}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Download PDF" title="Download PDF"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-on-surface-variant transition hover:bg-muted"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                          {/* Same cloneFrom navigation the detail page's own
                              Clone button uses — nothing is created until Save,
                              so there's nothing here for RBAC to block; the
                              create route it lands on already enforces it. */}
                          <button
                            type="button"
                            onClick={() => setAdvanceQuotation(q)}
                            aria-label="Record advance payment" title="Record advance payment"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-emerald-600/30 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition"
                          >
                            <Wallet className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`${ROUTES.quotationNew}?cloneFrom=${q._id}&type=quotation`)
                            }
                            aria-label="Clone quotation" title="Clone quotation"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-on-surface-variant transition hover:bg-muted"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer — the table only ever holds one page's worth
              of rows in the DOM, however large the real order history is. */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted px-4 py-3">
              <span className="text-data-xs font-semibold text-on-surface-variant">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-lg border border-border bg-card px-2 py-1 text-data-xs font-semibold text-on-surface-variant"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card text-on-surface-variant transition hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[70px] text-center text-data-xs font-bold text-on-surface-variant">
                  Page {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card text-on-surface-variant transition hover:bg-muted disabled:opacity-40"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INTERACTIVE IN-APP PDF / DOCUMENT PREVIEW MODAL */}
      {previewQuotation && (
        <div className="backdrop-blur-xs animate-in fade-in fixed inset-0 z-[1000] flex items-center justify-center bg-inverse-surface/60 p-3 duration-200 md:p-6">
          <div className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-border bg-muted px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-container font-black text-on-primary-container">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-on-surface">
                    Quotation Document:{' '}
                    {previewQuotation.orderNumber ||
                      previewQuotation.documentNumber ||
                      previewQuotation._id.slice(-6)}
                  </h3>
                  <p className="text-data-xs font-medium text-on-surface-variant">
                    {previewQuotation.customerName} · Total:{' '}
                    {formatDocTotal(previewQuotation as never)}
                  </p>
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="flex items-center gap-2">
                <a
                  href={`/api/documents/quotation/${previewQuotation._id}?format=pdf&download=1`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-primary-dim"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Download PDF</span>
                </a>

                <a
                  href={`/api/documents/quotation/${previewQuotation._id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shadow-2xs flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-semibold text-on-surface-variant transition hover:bg-muted"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span>Open in Tab</span>
                </a>

                <button
                  onClick={() => setPreviewQuotation(null)}
                  className="ml-2 rounded-xl p-1.5 text-on-surface-subtle transition hover:bg-muted hover:text-on-surface-variant"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Embedded Iframe Document Preview */}
            <div className="flex-1 overflow-hidden bg-muted p-2 md:p-4">
              <iframe
                src={`/api/documents/quotation/${previewQuotation._id}`}
                className="h-full w-full rounded-xl border border-border bg-card shadow-inner"
                title={`Quotation Preview ${previewQuotation._id}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lost Reason Modal for Quotation */}
      {selectedForLostModal && (
        <LostReasonModal
          deal={
            {
              title:
                selectedForLostModal.orderNumber ||
                selectedForLostModal.customerName ||
                'Quotation',
            } as any
          }
          onCancel={() => setSelectedForLostModal(null)}
          onConfirm={async (payload) => {
            try {
              await markLost.mutateAsync({
                id: selectedForLostModal._id,
                payload: {
                  ...payload,
                  closeDeal: payload.closeDeal,
                },
              });
              toast.success('Quotation marked as Closed Lost.');
              setSelectedForLostModal(null);
            } catch (err) {
              toast.error(errorText(err, 'Failed to mark quotation as lost.'));
            }
          }}
          isSaving={markLost.isPending}
          closeDealChoice={
            selectedForLostModal.crmDealId || selectedForLostModal.crmLeadId
              ? { dealTitle: selectedForLostModal.customerName || 'the linked deal' }
              : undefined
          }
        />
      )}

      {advanceQuotation && (
        <RecordAdvanceModal
          open={!!advanceQuotation}
          onClose={() => setAdvanceQuotation(null)}
          orderId={advanceQuotation._id}
          orderNumber={advanceQuotation.orderNumber || advanceQuotation.documentNumber}
          documentType={advanceQuotation.documentType || 'quotation'}
          clientName={advanceQuotation.customerName}
          grandTotal={advanceQuotation.grandTotal}
          currentAmountPaid={(advanceQuotation as any).amountPaid || 0}
          onSuccess={() => setAdvanceQuotation(null)}
        />
      )}
    </div>
  );
}
